<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

// Contact owner
const agents = computed(() => store.getters['agents/getAgents'] || []);
const contactOwnerName = computed(() => {
  const userId = currentContact.value?.user_id;
  if (!userId) return null;
  const agent = agents.value.find(a => a.id === userId);
  return agent?.name || null;
});

const conversationDisplayId = computed(
  () => currentChat.value?.display_id || currentChat.value?.id
);

const idCopied = ref(false);
const copyConversationId = () => {
  if (!conversationDisplayId.value) return;
  navigator.clipboard.writeText(`#${conversationDisplayId.value}`);
  idCopied.value = true;
  setTimeout(() => { idCopied.value = false; }, 2000);
};

onMounted(() => {
  if (!agents.value.length) {
    store.dispatch('agents/get');
  }
});
</script>

<template>
  <div ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12">
    <div class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1">
      <BackButton v-if="showBackButton" :back-url="backButtonUrl" class="ltr:mr-2 rtl:ml-2" />
      <Avatar :name="currentContact.name" :src="currentContact.thumbnail" :size="32"
        :status="currentContact.availability_status" hide-offline-status rounded-full />
      <div class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2">
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span class="text-sm font-medium truncate leading-tight text-n-slate-12">
            {{ currentContact.name }}
          </span>
          <button
            v-if="conversationDisplayId"
            v-tooltip="idCopied ? 'Copiado!' : 'Copiar ID'"
            class="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-10 hover:bg-n-alpha-3 hover:text-n-slate-12 transition-colors cursor-pointer"
            @click.stop="copyConversationId"
          >
            #{{ conversationDisplayId }}
          </button>
          <fluent-icon v-if="!isHMACVerified" v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')" size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0" icon="warning" />
        </div>

        <div
          class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap">
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span class="flex items-center gap-1 font-medium text-n-slate-10">
            <span class="i-lucide-briefcase size-3" />
            {{ contactOwnerName || 'Sem responsável' }}
          </span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap">
      <SLACardLabel v-if="hasSlaPolicyId" :chat="chat" show-extended-info :parent-width="width"
        class="hidden md:flex" />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
